import { c as _c } from "react-compiler-runtime";
import { useCellSelectionDispatch } from '../../redux/reduxHooks';
import { isMacOs } from '../utils/isMacOs';
import { selectCell, addRange, extendActiveRange, dragOver, startFillDrag, clearCellSelection } from '../redux/cellSelectionSlice';
import { CELL_SELECTION_IGNORE_PLAIN_CLICK_ATTRIBUTE } from '../../table/constants/cellSelectionAttributes';

// Hit-test size (px) for the fill handle, measured inward from the cell's
// inner bottom-right corner (rect.right / rect.bottom). Intentionally larger
// than the visible 8×8px dot (::after in StyledTableComponents) to make the
// small drag target easier to grab.
const FILL_HANDLE_HIT_SIZE = 12;
const isWithinFillHandle = e => {
  const rect = e.currentTarget.getBoundingClientRect();
  return e.clientX >= rect.right - FILL_HANDLE_HIT_SIZE && e.clientY >= rect.bottom - FILL_HANDLE_HIT_SIZE;
};

/**
 * Builds the mousedown/mouseenter handlers for a single cell at
 * `{ rowIndex, columnIndex }`. Returned object is spread onto the cell's
 * `requiredCellProps` (and from there onto the consumer's `<td>`).
 *
 * - mousedown maps the modifier keys to the three spreadsheet gestures:
 *   shift extends the active range, Cmd/Ctrl adds a new disjoint range (or
 *   deselects the cell if it is already selected), and a plain click starts a
 *   fresh single-cell selection.
 * - mouseenter extends the active range while a drag is in progress (the
 *   reducer no-ops otherwise).
 *
 * `preventDefault` on mousedown suppresses the browser's native text selection
 * so dragging across cells highlights cells, not text.
 */
export const useCellSelectionHandlers = (rowIndex, columnIndex, isFillHandleCell) => {
  const $ = _c(13);
  const dispatch = useCellSelectionDispatch();
  let t0;
  if ($[0] !== columnIndex || $[1] !== rowIndex) {
    t0 = {
      rowIndex,
      columnIndex
    };
    $[0] = columnIndex;
    $[1] = rowIndex;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const coord = t0;
  let t1;
  if ($[3] !== coord || $[4] !== dispatch || $[5] !== isFillHandleCell) {
    t1 = e => {
      if (e.button !== 0) {
        return;
      }
      if (isFillHandleCell && isWithinFillHandle(e)) {
        e.preventDefault();
        dispatch(startFillDrag());
        return;
      }
      if (e.shiftKey) {
        e.preventDefault();
        dispatch(extendActiveRange(coord));
        return;
      }
      if (isMacOs() ? e.metaKey : e.ctrlKey) {
        e.preventDefault();
        dispatch(addRange(coord));
        return;
      }
      const isOnIgnoredElement = e.target instanceof Element && !!e.target.closest(`[${CELL_SELECTION_IGNORE_PLAIN_CLICK_ATTRIBUTE}]`);
      if (isOnIgnoredElement) {
        dispatch(clearCellSelection());
        return;
      }
      e.preventDefault();
      dispatch(selectCell(coord));
    };
    $[3] = coord;
    $[4] = dispatch;
    $[5] = isFillHandleCell;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== coord || $[8] !== dispatch) {
    t2 = e_0 => {
      const isLeftButtonDown = (e_0.buttons & 1) !== 0;
      if (isLeftButtonDown) {
        dispatch(dragOver(coord));
      }
    };
    $[7] = coord;
    $[8] = dispatch;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t1 || $[11] !== t2) {
    t3 = {
      onMouseDown: t1,
      onMouseEnter: t2
    };
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
};